/**
 * RUN 84 - what the extension push draws: one push control per competition on the day's head, the
 * question that runs the whole press, and the strip its answer leaves.
 *
 * The question is the page's one dialogue idiom (`.overridebox`, `role="alertdialog"`,
 * `aria-modal`, its body as its description, Tab kept inside it by the panel's handler) and it is
 * modal from `Confirm Push` to the answer: it asks (section A9's copy), it says where the press is
 * while it runs - the waiting for the TD's Confirm in the extension's own window, and until when
 * the day is held, on the tournament's clock - and it gives the answer, match by match, with the
 * extension's own words and any collateral warning. It has no Escape, as none of the page's
 * questions has: while the press runs there is nothing on this page to press, because the only
 * confirm and the only cancel are the extension's own (`12-tms-push-extension.md` section 2).
 *
 * The panel above owns every piece of state and every write; this file only draws.
 *
 * **The day push** (`12-tms-push-extension.md` section 6c; the TD's Decision #5 = B, 19 Sep 2026):
 * `Push Day To TMS` stands first among the push controls, and asks its own question in the same frame
 * and idiom - section A9's batch once per competition of the day, Men's first, and the sentence that
 * one Confirm in the extension makes one TMS save per competition, one after the other, and publishes
 * nothing; then where the day push is; then its answer, one section per competition in the API's
 * words, with a stop in the extension's.
 *
 * Source is ASCII: typographic characters come from `tmsPushCopy.ts`'s constants or entities.
 */

import type { TmsCompetitionId } from "@hockeytd/shared";

import type {
  ExtensionPushLeftOutBody,
  ExtensionPushReportBody,
  ExtensionPushResultMessage,
} from "../../api/types.js";
import { dayLabel } from "../schedule/matchDays.js";
import {
  BLOCKED_TAIL,
  COLLATERAL_LEAD,
  MDASH,
  UMPIRES_UNTOUCHED,
  answerCollateral,
  answerHeadline,
  answerLines,
  answerTitle,
  batchLine,
  batchName,
  blockedLead,
  competitionWord,
  confirmHandsOver,
  confirmLabel,
  dayAnswerSummary,
  dayAnswerTitle,
  dayPressText,
  dayPressTitle,
  dayPressVerdict,
  dayStepSentence,
  dayStopWords,
  holdSentence,
  leftAsTheyAre,
  noResultText,
  noResultTitle,
  notTakenTitle,
  postsSentence,
  pushDayBlocked,
  pushDayConfirmLabel,
  pushDayHandsOver,
  pushDaySectionNote,
  pushDaySectionTitle,
  pushDayTitle,
  pushTitle,
  releaseRefusal,
  releasedLines,
  releasedText,
  replayedSentence,
  reportRefusal,
  separateBatch,
  stepSentence,
  type AnswerLine,
  type PushActionView,
  type PushBatchPreview,
  type PushDayActionView,
  type PushDayPreview,
} from "./tmsPushCopy.js";
import type { RememberedPress, TmsDayPushPress, TmsPushEnding, TmsPushStep } from "./tmsPushFlow.js";

/* -- Ids the panel hands the focus to ------------------------------------------------------------ */

/** One competition's push control, so `Cancel` can give the focus back to the press that asked. */
export function pushButtonId(competitionId: TmsCompetitionId): string {
  return `push-${competitionId}`;
}

/** The reason one competition's control carries, where it is its own. */
export function pushWhyId(competitionId: TmsCompetitionId): string {
  return `push-why-${competitionId}`;
}

/** The reason every push control on the day shares, where they share one. */
export const PUSH_GROUP_WHY_ID = "push-why";

/**
 * The note every push control carries while the TMS Push extension has not answered - a note, not a
 * reason: the controls stay pressable, and the press asks again (frontend review M2).
 */
export const PUSH_GROUP_NOTE_ID = "push-note";

/** The answer's own sentence, which takes the focus when the answer arrives (frontend review M3). */
export const PUSH_OUTCOME_ID = "pushOutcome";

/** The answer's own way out. */
export const PUSH_CLOSE_ID = "push-close";

/** The day push (section 6c): `Push Day To TMS`, so `Cancel` can give the focus back to it. */
export const PUSH_DAY_BUTTON_ID = "push-day";

/** The day push control's own reason, where the group's does not say it. */
export const PUSH_DAY_WHY_ID = "push-day-why";

/** The day push's answer, in a paragraph, which takes the focus when the answer arrives. */
export const PUSH_DAY_OUTCOME_ID = "pushDayOutcome";

/** The day push answer's own way out. */
export const PUSH_DAY_CLOSE_ID = "push-day-close";

/* -- The controls ---------------------------------------------------------------------------------- */

/**
 * One push control per competition played on the day, beside the day's other presses. A control
 * that cannot be pressed says why ON the control - its accessible description, and printed beside
 * it - rather than only being greyed; it stays reachable so that its reason can be read.
 */
export function TmsPushActions({
  actions,
  groupReason,
  groupNote = null,
  onPush,
  dayAction = null,
  onPushDay,
}: {
  actions: readonly PushActionView[];
  groupReason: string | null;
  /** Said beside the controls without stopping them - the extension did not answer (M2). */
  groupNote?: string | null;
  onPush: (competitionId: TmsCompetitionId) => void;
  /**
   * The day push (`12-tms-push-extension.md` section 6c): `Push Day To TMS`, drawn first, beside the
   * per-competition controls, which stay. Null where the day has no match.
   */
  dayAction?: PushDayActionView | null;
  onPushDay?: () => void;
}): React.JSX.Element {
  const dayWhy =
    dayAction === null
      ? undefined
      : dayAction.available || dayAction.pushing
        ? dayAction.available && groupNote !== null
          ? PUSH_GROUP_NOTE_ID
          : undefined
        : groupReason !== null
          ? PUSH_GROUP_WHY_ID
          : dayAction.reason === null
            ? undefined
            : PUSH_DAY_WHY_ID;
  return (
    <span className="tmspush" data-howto="push-tms" data-testid="push-actions">
      {dayAction === null ? null : (
        <span className="tmspush__act tmspush__act--day" data-howto="push-day">
          <button
            type="button"
            className="gbtn"
            id={PUSH_DAY_BUTTON_ID}
            data-testid={PUSH_DAY_BUTTON_ID}
            aria-disabled={dayAction.available ? undefined : true}
            aria-describedby={dayWhy}
            aria-busy={dayAction.pushing ? true : undefined}
            onClick={() => {
              if (dayAction.available) onPushDay?.();
            }}
          >
            {dayAction.label}
          </button>
          {groupReason !== null || dayAction.reason === null ? null : (
            <span className="tmspush__why" id={PUSH_DAY_WHY_ID} data-testid={PUSH_DAY_WHY_ID}>
              {dayAction.reason}
            </span>
          )}
        </span>
      )}
      {actions.map((action) => {
        const why =
          action.available || action.pushing
            ? action.available && groupNote !== null
              ? PUSH_GROUP_NOTE_ID
              : undefined
            : groupReason !== null
              ? PUSH_GROUP_WHY_ID
              : action.reason === null
                ? undefined
                : pushWhyId(action.competitionId);
        return (
          <span className="tmspush__act" key={action.competitionId}>
            <button
              type="button"
              className="gbtn"
              id={pushButtonId(action.competitionId)}
              data-testid={pushButtonId(action.competitionId)}
              aria-disabled={action.available ? undefined : true}
              aria-describedby={why}
              aria-busy={action.pushing ? true : undefined}
              onClick={() => {
                if (action.available) onPush(action.competitionId);
              }}
            >
              {action.label}
            </button>
            {groupReason !== null || action.reason === null ? null : (
              <span
                className="tmspush__why"
                id={pushWhyId(action.competitionId)}
                data-testid={pushWhyId(action.competitionId)}
              >
                {action.reason}
              </span>
            )}
          </span>
        );
      })}
      {groupReason === null || actions.length === 0 ? null : (
        <span className="tmspush__why" id={PUSH_GROUP_WHY_ID} data-testid={PUSH_GROUP_WHY_ID}>
          {groupReason}
        </span>
      )}
      {groupNote === null || groupReason !== null || actions.length === 0 ? null : (
        <span className="tmspush__why tmspush__note" id={PUSH_GROUP_NOTE_ID} data-testid={PUSH_GROUP_NOTE_ID}>
          {groupNote}
        </span>
      )}
    </span>
  );
}

/* -- The question ---------------------------------------------------------------------------------- */

/** The endings the question stays open to say. The others close it and speak on the page. */
export type TmsPushAnsweredEnding = Extract<
  TmsPushEnding,
  | { kind: "reported" }
  | { kind: "report-refused" }
  | { kind: "no-result" }
  | { kind: "released" }
  | { kind: "release-refused" }
>;

/** Where the push question is. */
export type TmsPushFlowState =
  | { readonly kind: "confirm"; readonly competitionId: TmsCompetitionId }
  | {
      readonly kind: "working";
      readonly competitionId: TmsCompetitionId;
      readonly day: string;
      readonly step: TmsPushStep;
      readonly press: RememberedPress | null;
      /** Which try of the report this is, and of how many - 1 of 1 for every other step. */
      readonly attempt: number;
      readonly attempts: number;
    }
  | {
      readonly kind: "answered";
      readonly competitionId: TmsCompetitionId;
      readonly day: string;
      readonly ending: TmsPushAnsweredEnding;
      /** Whether the hold had lapsed when the press ended - for the sentences that say so. */
      readonly lapsed: boolean;
    };

/** The ids of the paragraphs a question is drawing now, joined for `aria-describedby`. */
function describedBy(ids: readonly (string | null)[]): string {
  return ids.filter((id): id is string => id !== null).join(" ");
}

/**
 * The collateral warnings, prominent: in the extension's own words, one per line - an alert inside
 * the question, where it is news; plain on the strip, which repeats what was announced.
 */
function Collateral({
  warnings,
  id,
}: {
  warnings: readonly string[];
  /** Inside the question: its id, and an alert. On the strip it is neither. */
  id: string | null;
}): React.JSX.Element | null {
  if (warnings.length === 0) return null;
  return (
    <div
      className="tmspush-collateral"
      {...(id === null ? {} : { id, role: "alert" })}
      data-testid="push-collateral"
    >
      <b>Check TMS {MDASH}</b> {COLLATERAL_LEAD}
      <ul>
        {warnings.map((warning, index) => (
          <li key={`${String(index)}-${warning}`}>{warning}</li>
        ))}
      </ul>
    </div>
  );
}

/** The answer, match by match. `id` only inside the question, which it describes. */
export function TmsPushAnswerLines({
  lines,
  id,
}: {
  lines: readonly AnswerLine[];
  id: string | null;
}): React.JSX.Element {
  return (
    <ul className="tmspush-answer" {...(id === null ? {} : { id })} data-testid="push-answer-lines">
      {lines.map((line) => (
        <li key={line.key} className={`tmspush-answer__line is-${line.tone}`} data-testid={`push-line-${line.key}`}>
          <b className="num">{line.match}</b>{" "}
          <span className="tmspush-answer__verdict">{line.verdict}</span>
          {line.extensionSaid === null ? null : (
            <span className="tmspush-answer__said">The extension said: {line.extensionSaid}</span>
          )}
          {line.recorded === null ? null : (
            <span className="tmspush-answer__said">Recorded: {line.recorded}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

export interface TmsPushQuestionProps {
  flow: TmsPushFlowState;
  /** The batch as the page sees it - for the confirmation only. */
  batch: PushBatchPreview | null;
  /** A write is out: the answers that write are disabled. */
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
  onReportAgain: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}

/**
 * The push question, in whichever of its three moments it is: asking, running, answered.
 *
 * It can hold the focus itself (`tabIndex={-1}`, out of the tab order) and is handed it only while
 * the press runs, when it has no button to give it to; its words are then its description, and the
 * step line inside it is a live region. Once it has an answer, the answer's own sentence takes the
 * focus (`tabIndex={-1}` too): it is read out, and brought into view on a phone, where the answer
 * can be taller than the screen. Tab from there goes to the question's own buttons, never behind it.
 */
export function TmsPushQuestion({
  flow,
  batch,
  busy,
  onConfirm,
  onCancel,
  onClose,
  onReportAgain,
  onKeyDown,
}: TmsPushQuestionProps): React.JSX.Element | null {
  const frame = (
    title: string,
    described: readonly (string | null)[],
    body: React.ReactNode,
    acts: React.ReactNode,
  ): React.JSX.Element => (
    <div
      className="overridebox tmspush-q"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="pushTitle"
      aria-describedby={describedBy(described)}
      data-testid="push-prompt"
      data-phase={flow.kind}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <b id="pushTitle" data-testid="push-prompt-title">
        {title}
      </b>
      {body}
      {acts === null ? null : <div className="overridebox__acts">{acts}</div>}
    </div>
  );

  if (flow.kind === "confirm") {
    if (batch === null) return null;
    const word = competitionWord(batch.competitionId);
    const left = leftAsTheyAre(batch);
    const blocked = batch.countryFails.length > 0;
    const separate = separateBatch(batch);
    return frame(
      pushTitle(batch),
      ["pushBatch", "pushList", left === "" ? null : "pushLeft", "pushNote", "pushHow"],
      <>
        <p id="pushBatch" data-testid="push-prompt-batch">
          TMS batches appointments by <b>day and competition</b>, so pushing <b>{word}</b> pushes
          every <b>{word}</b> match on <b>{dayLabel(batch.day)}</b>:
        </p>
        <ul id="pushList" className="tmspush-q__list" data-testid="push-prompt-list">
          {batch.sendable.map((fixture) => (
            <li
              key={fixture.tmsMatchDbId}
              className={fixture.competitionGender === "w" ? "is-women" : "is-men"}
              data-testid={`push-prompt-match-${String(fixture.tmsMatchDbId)}`}
            >
              {batchLine(fixture)}
            </li>
          ))}
        </ul>
        {left === "" ? null : (
          <p id="pushLeft" data-testid="push-prompt-left">
            {left}
          </p>
        )}
        {blocked ? (
          <p id="pushNote" data-testid="push-prompt-note">
            <b>{blockedLead(batch)}</b> {BLOCKED_TAIL}
          </p>
        ) : (
          <p id="pushNote" data-testid="push-prompt-note">
            {separate === null ? null : (
              <>
                The <b>{separate.bold}</b>
                {separate.rest}{" "}
              </>
            )}
            {UMPIRES_UNTOUCHED}
          </p>
        )}
        <p id="pushHow" data-testid="push-prompt-how">
          {confirmHandsOver(batch)}
        </p>
      </>,
      <>
        <button
          type="button"
          className="gbtn gbtn--solid"
          data-testid="push-confirm"
          disabled={busy || blocked}
          onClick={onConfirm}
        >
          {confirmLabel(batch)}
        </button>
        <button type="button" className="gbtn" onClick={onCancel}>
          Cancel
        </button>
      </>,
    );
  }

  if (flow.kind === "working") {
    const hold = holdSentence(flow.press);
    return frame(
      `Pushing ${competitionWord(flow.competitionId)} ${MDASH} ${dayLabel(flow.day)}`,
      ["pushStep", hold === null ? null : "pushHold"],
      <>
        {/* A live region that stays while the press runs: each step replaces its words. */}
        <p id="pushStep" role="status" className="tmspush-q__step" data-testid="push-prompt-step">
          {stepSentence(flow.step, flow.attempt, flow.attempts)}
        </p>
        {hold === null ? null : (
          <p id="pushHold" data-testid="push-prompt-hold">
            {hold}
          </p>
        )}
      </>,
      null,
    );
  }

  const ending = flow.ending;
  const close = (
    <button
      type="button"
      className="gbtn"
      id={PUSH_CLOSE_ID}
      data-testid={PUSH_CLOSE_ID}
      onClick={onClose}
    >
      Close
    </button>
  );

  if (ending.kind === "reported") {
    const replayed = replayedSentence(ending.answer);
    return frame(
      answerTitle(ending.answer, ending.result, ending.press),
      [
        "pushOutcome",
        "pushPosts",
        answerCollateral(ending.answer, ending.result).length === 0 ? null : "pushCollateral",
        "pushMatches",
      ],
      <>
        <p id={PUSH_OUTCOME_ID} className="tmspush-q__outcome" data-testid="push-answer-outcome" tabIndex={-1}>
          {answerHeadline(ending.answer, ending.result, ending.press)}
        </p>
        <p id="pushPosts" data-testid="push-answer-posts">
          {postsSentence(ending.result, ending.press)}
          {replayed === null ? null : ` ${replayed}`}
        </p>
        <Collateral warnings={answerCollateral(ending.answer, ending.result)} id="pushCollateral" />
        <TmsPushAnswerLines lines={answerLines(ending.answer, ending.result)} id="pushMatches" />
      </>,
      close,
    );
  }

  if (ending.kind === "report-refused") {
    return frame(
      `Result Not Recorded ${MDASH} ${batchName(ending.press.competitionId, ending.press.day)}`,
      ["pushOutcome", answerCollateral(null, ending.result).length === 0 ? null : "pushCollateral"],
      <>
        <p id={PUSH_OUTCOME_ID} className="tmspush-q__outcome" data-testid="push-answer-outcome" tabIndex={-1}>
          {reportRefusal(ending.error, ending.press, ending.retryable)}
        </p>
        <Collateral warnings={answerCollateral(null, ending.result)} id="pushCollateral" />
      </>,
      <>
        {ending.retryable ? (
          <button
            type="button"
            className="gbtn gbtn--solid"
            data-testid="push-report-again"
            disabled={busy}
            onClick={onReportAgain}
          >
            Report Again
          </button>
        ) : null}
        {close}
      </>,
    );
  }

  if (ending.kind === "released") {
    return frame(
      notTakenTitle(ending.press, true),
      ["pushOutcome", "pushMatches"],
      <>
        <p id={PUSH_OUTCOME_ID} className="tmspush-q__outcome" data-testid="push-answer-outcome" tabIndex={-1}>
          {releasedText(ending.notTaken, ending.press)}
        </p>
        <TmsPushAnswerLines lines={releasedLines(ending.answer)} id="pushMatches" />
      </>,
      close,
    );
  }

  if (ending.kind === "release-refused") {
    return frame(
      notTakenTitle(ending.press, false),
      ["pushOutcome"],
      <p id={PUSH_OUTCOME_ID} className="tmspush-q__outcome" data-testid="push-answer-outcome" tabIndex={-1}>
        {releaseRefusal(ending.error, ending.notTaken, ending.press)}
      </p>,
      close,
    );
  }

  return frame(
    noResultTitle(ending.press, ending.recorded),
    ["pushOutcome"],
    <p id={PUSH_OUTCOME_ID} className="tmspush-q__outcome" data-testid="push-answer-outcome" tabIndex={-1}>
      {noResultText(ending.why, ending.press, flow.lapsed, ending.recorded)}
    </p>,
    close,
  );
}

/* -- The strip the answer leaves ----------------------------------------------------------------- */

/** What a reported push leaves on the page once its question is closed, until the next push. */
export interface TmsPushStripState {
  readonly press: RememberedPress;
  readonly result: ExtensionPushResultMessage;
  readonly answer: ExtensionPushReportBody;
}

/**
 * The answer, match by match, and any collateral warning, beside the notice that states it in one
 * sentence - so the per-match detail and the warnings are not lost when the question closes.
 */
export function TmsPushResultStrip({ strip }: { strip: TmsPushStripState }): React.JSX.Element {
  return (
    <div className="stagestrip tmspush-strip" data-testid="push-result">
      <span className="kicker">
        TMS Push {MDASH} {batchName(strip.press.competitionId, strip.press.day)}
      </span>
      <Collateral warnings={answerCollateral(strip.answer, strip.result)} id={null} />
      <TmsPushAnswerLines lines={answerLines(strip.answer, strip.result)} id={null} />
    </div>
  );
}

/* -- The day push's question (section 6c) ------------------------------------------------------ */

/** Where the day push's question is. */
export type TmsDayPushFlowState =
  | { readonly kind: "confirm" }
  | {
      readonly kind: "working";
      readonly day: string;
      readonly step: TmsPushStep;
      /** The press the step is about, where it is about one competition's press. */
      readonly press: RememberedPress | null;
      /** A press whose hold the question prints, once the prepare has answered: one hold, one instant. */
      readonly hold: RememberedPress | null;
      /** Which try of a report this is, and of how many - 1 of 1 for every other step. */
      readonly attempt: number;
      readonly attempts: number;
      /** The presses that have their ending already, in the order they ended. */
      readonly ended: readonly TmsDayPushPress[];
    }
  | {
      readonly kind: "answered";
      readonly day: string;
      readonly presses: readonly TmsDayPushPress[];
      readonly leftOut: readonly ExtensionPushLeftOutBody[];
      /** The press ids whose hold had run out when the day push ended - for the sentences that say so. */
      readonly lapsed: readonly string[];
      /** Every answer the API gave the day push, in the order they arrived (fix round 1, P1). */
      readonly answers: readonly ExtensionPushReportBody[];
      /** Recovered after a reload: it answers only for the presses the tab remembered (P2). */
      readonly recovered: boolean;
    };

export interface TmsDayPushQuestionProps {
  flow: TmsDayPushFlowState;
  /** The day as the page sees it - for the confirmation only. */
  preview: PushDayPreview | null;
  /** A write is out: the answers that write are disabled. */
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
  onReportAgain: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}

/** One competition of an answered day push: its own section, in the single press's words for its ending. */
function DayPressSection({ entry, lapsed }: { entry: TmsDayPushPress; lapsed: boolean }): React.JSX.Element {
  const { press, ending } = entry;
  const key = press.competitionId;
  const headId = `pushDayPress-${key}`;
  let body: React.ReactNode;
  if (ending.kind === "reported") {
    // A day push that STOPPED before this competition - or its window closed before its turn - says
    // so in the extension's own words, once, above the matches (section 6c, "a stop is shown in the
    // extension's words").
    const stop = dayStopWords(ending.result);
    const replayed = replayedSentence(ending.answer);
    // Fix round 1 (P3): the stop stands ONCE, above the matches - a line that carries the same words
    // does not repeat them.
    const lines = answerLines(ending.answer, ending.result).map((line) =>
      stop !== null && line.extensionSaid === stop ? { ...line, extensionSaid: null } : line,
    );
    body = (
      <>
        {stop === null ? null : (
          <p className="tmspush-day__stop" data-testid={`push-day-stop-${key}`}>
            <b>The TMS Push extension said:</b> {stop}
          </p>
        )}
        <p data-testid={`push-day-posts-${key}`}>
          {postsSentence(ending.result, ending.press)}
          {replayed === null ? null : ` ${replayed}`}
        </p>
        <Collateral warnings={answerCollateral(ending.answer, ending.result)} id={`pushDayCollateral-${key}`} />
        <TmsPushAnswerLines lines={lines} id={null} />
      </>
    );
  } else {
    body = (
      <>
        <p data-testid={`push-day-text-${key}`}>{dayPressText(entry, lapsed)}</p>
        {ending.kind === "released" ? <TmsPushAnswerLines lines={releasedLines(ending.answer)} id={null} /> : null}
        {ending.kind === "report-refused" ? (
          <Collateral warnings={answerCollateral(null, ending.result)} id={`pushDayCollateral-${key}`} />
        ) : null}
      </>
    );
  }
  return (
    <section className="tmspush-day" aria-labelledby={headId} data-testid={`push-day-section-${key}`}>
      <h3 className="tmspush-day__head" id={headId}>
        {dayPressTitle(entry)}
      </h3>
      {body}
    </section>
  );
}

/**
 * The day push's question, in whichever of its three moments it is - asking, running, answered - in
 * the same frame and with the same rules as the single press's question: modal, its body its
 * description, its focus on `Cancel` as it opens, the frame itself holding the focus while the day push
 * runs (there is nothing on this page to press then), and the answer's own paragraph taking it when the
 * answer arrives.
 */
export function TmsDayPushQuestion({
  flow,
  preview,
  busy,
  onConfirm,
  onCancel,
  onClose,
  onReportAgain,
  onKeyDown,
}: TmsDayPushQuestionProps): React.JSX.Element | null {
  const frame = (
    title: string,
    described: readonly (string | null)[],
    body: React.ReactNode,
    acts: React.ReactNode,
  ): React.JSX.Element => (
    <div
      className="overridebox tmspush-q"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="pushDayTitle"
      aria-describedby={describedBy(described)}
      data-testid="push-day-prompt"
      data-phase={flow.kind}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <b id="pushDayTitle" data-testid="push-day-prompt-title">
        {title}
      </b>
      {body}
      {acts === null ? null : <div className="overridebox__acts">{acts}</div>}
    </div>
  );

  if (flow.kind === "confirm") {
    if (preview === null) return null;
    const blocked = pushDayBlocked(preview);
    return frame(
      pushDayTitle(preview),
      [
        "pushDayLead",
        ...preview.batches.map((batch) => `pushDayBatch-${batch.competitionId}`),
        "pushDayNote",
        "pushDayHow",
      ],
      <>
        <p id="pushDayLead" data-testid="push-day-lead">
          TMS batches appointments by <b>day and competition</b>, so the day push sends every
          competition of <b>{dayLabel(preview.day)}</b> that owes a push, each as a batch of its own:
        </p>
        {preview.batches.map((batch) => {
          const left = leftAsTheyAre(batch);
          const note = pushDaySectionNote(batch);
          return (
            <section
              key={batch.competitionId}
              id={`pushDayBatch-${batch.competitionId}`}
              className="tmspush-day"
              aria-labelledby={`pushDayBatchHead-${batch.competitionId}`}
              data-testid={`push-day-batch-${batch.competitionId}`}
            >
              <h3 className="tmspush-day__head" id={`pushDayBatchHead-${batch.competitionId}`}>
                {pushDaySectionTitle(batch)}
              </h3>
              {batch.sendable.length === 0 ? null : (
                <ul className="tmspush-q__list" data-testid={`push-day-list-${batch.competitionId}`}>
                  {batch.sendable.map((fixture) => (
                    <li
                      key={fixture.tmsMatchDbId}
                      className={fixture.competitionGender === "w" ? "is-women" : "is-men"}
                      data-testid={`push-day-match-${String(fixture.tmsMatchDbId)}`}
                    >
                      {batchLine(fixture)}
                    </li>
                  ))}
                </ul>
              )}
              {left === "" ? null : <p>{left}</p>}
              {batch.countryFails.length === 0 ? null : (
                <p data-testid={`push-day-blocked-${batch.competitionId}`}>
                  <b>{blockedLead(batch)}</b> {BLOCKED_TAIL}
                </p>
              )}
              {note === null ? null : <p data-testid={`push-day-note-${batch.competitionId}`}>{note}</p>}
            </section>
          );
        })}
        <p id="pushDayNote" data-testid="push-day-umpires">
          {UMPIRES_UNTOUCHED}
        </p>
        <p id="pushDayHow" data-testid="push-day-how">
          {pushDayHandsOver(preview)}
        </p>
      </>,
      <>
        <button
          type="button"
          className="gbtn gbtn--solid"
          data-testid="push-day-confirm"
          disabled={busy || blocked}
          onClick={onConfirm}
        >
          {pushDayConfirmLabel(preview)}
        </button>
        <button type="button" className="gbtn" onClick={onCancel}>
          Cancel
        </button>
      </>,
    );
  }

  if (flow.kind === "working") {
    const hold = holdSentence(flow.hold);
    return frame(
      `Pushing The Day ${MDASH} ${dayLabel(flow.day)}`,
      ["pushDayStep", hold === null ? null : "pushDayHold"],
      <>
        {/* One live region while the day push runs: each step replaces its words. */}
        <p id="pushDayStep" role="status" className="tmspush-q__step" data-testid="push-day-prompt-step">
          {dayStepSentence(flow.step, flow.press, flow.attempt, flow.attempts)}
        </p>
        {hold === null ? null : (
          <p id="pushDayHold" data-testid="push-day-prompt-hold">
            {hold}
          </p>
        )}
        {/* Fix round 2 (R2): the competitions answered while the rest run are announced from a live
            region that is in the question from the moment the day push runs - as the page's own
            notice region is (RUN 82A) - so the FIRST answer is announced too: a region inserted with
            its first words already in it is one a screen reader is entitled to miss. Only its list
            comes and goes; each competition's verdict is added inside it, and read alone - polite,
            not atomic, so an addition never re-reads the verdicts before it. */}
        <div aria-live="polite" data-testid="push-day-ended-region">
          {flow.ended.length === 0 ? null : (
            <ul className="tmspush-day__ended" data-testid="push-day-ended">
              {flow.ended.map((entry) => (
                <li key={entry.press.pressId}>{dayPressVerdict(entry)}</li>
              ))}
            </ul>
          )}
        </div>
      </>,
      null,
    );
  }

  const retryable = flow.presses.some(
    ({ ending }) => ending.kind === "report-refused" && ending.retryable,
  );
  return frame(
    dayAnswerTitle(flow.presses, flow.day, flow.recovered),
    [PUSH_DAY_OUTCOME_ID],
    <>
      <p id={PUSH_DAY_OUTCOME_ID} className="tmspush-q__outcome" data-testid="push-day-outcome" tabIndex={-1}>
        {dayAnswerSummary(flow.presses, flow.leftOut, flow.day, flow.recovered)}
      </p>
      {flow.presses.map((entry) => (
        <DayPressSection
          key={entry.press.pressId}
          entry={entry}
          lapsed={flow.lapsed.includes(entry.press.pressId)}
        />
      ))}
    </>,
    <>
      {retryable ? (
        <button
          type="button"
          className="gbtn gbtn--solid"
          data-testid="push-day-report-again"
          disabled={busy}
          onClick={onReportAgain}
        >
          Report Again
        </button>
      ) : null}
      <button
        type="button"
        className="gbtn"
        id={PUSH_DAY_CLOSE_ID}
        data-testid={PUSH_DAY_CLOSE_ID}
        onClick={onClose}
      >
        Close
      </button>
    </>,
  );
}
